import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Crown, User, ShieldCheck, PencilSimple } from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import { useAdmin } from "@/context/AdminContext";

// Human-readable labels for module keys served by the backend.
const MODULE_LABELS = {
    overview: "Resumen",
    kitchen: "Cocina",
    orders: "Pedidos",
    pos: "POS",
    menu: "Menú",
    accounting: "Contabilidad",
    analytics: "Analítica",
    subscribers: "Suscriptores",
    reports: "Reportes",
    users: "Usuarios",
    printer: "Impresora",
    settings: "Ajustes",
    delivery: "Delivery",
};

const DEFAULT_STAFF_MODULES = ["overview", "kitchen", "orders", "pos"];

const emptyForm = () => ({
    email: "",
    password: "",
    role: "staff",
    name: "",
    modules: [...DEFAULT_STAFF_MODULES],
});

export default function Users() {
    const { user, isOwner } = useAdmin();
    const [users, setUsers] = useState([]);
    const [availableModules, setAvailableModules] = useState(Object.keys(MODULE_LABELS));
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // user being edited (modules)
    const [editModules, setEditModules] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);

    const load = () =>
        api.get("/admin/users", { headers: authHeaders() })
            .then((r) => {
                setUsers(r.data.users);
                if (Array.isArray(r.data.available_modules) && r.data.available_modules.length) {
                    setAvailableModules(r.data.available_modules);
                }
            })
            .catch(() => {});

    useEffect(() => {
        if (isOwner) load();
    }, [isOwner]);

    if (!isOwner) {
        return (
            <div data-testid="users-forbidden" className="bg-white rounded-3xl border border-border p-12 text-center">
                <Crown size={48} weight="duotone" className="mx-auto text-primary opacity-50 mb-4" />
                <h2 className="font-display font-black text-2xl">Owner only</h2>
                <p className="text-muted-foreground mt-2">Only the owner can manage admin users.</p>
            </div>
        );
    }

    const toggleFormModule = (key) => {
        setForm((f) => ({
            ...f,
            modules: f.modules.includes(key)
                ? f.modules.filter((m) => m !== key)
                : [...f.modules, key],
        }));
    };

    const toggleEditModule = (key) => {
        setEditModules((prev) =>
            prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key],
        );
    };

    const create = async () => {
        if (!form.email || !form.password) {
            toast.error("Email y contraseña son requeridos");
            return;
        }
        if (form.role === "staff" && form.modules.length === 0) {
            toast.error("Selecciona al menos un módulo para el staff");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                email: form.email,
                password: form.password,
                role: form.role,
                name: form.name,
                modules: form.role === "owner" ? availableModules : form.modules,
            };
            await api.post("/admin/users", payload, { headers: authHeaders() });
            toast.success("Usuario creado");
            setOpen(false);
            setForm(emptyForm());
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo crear");
        } finally {
            setBusy(false);
        }
    };

    const openEdit = (u) => {
        setEditing(u);
        setEditModules(u.modules || []);
    };

    const saveModules = async () => {
        if (!editing) return;
        if (editModules.length === 0) {
            toast.error("Selecciona al menos un módulo");
            return;
        }
        setBusy(true);
        try {
            await api.put(
                `/admin/users/${editing.id}/modules`,
                { modules: editModules },
                { headers: authHeaders() },
            );
            toast.success("Permisos actualizados");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`¿Eliminar ${u.email}?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`, { headers: authHeaders() });
            toast.success("Usuario eliminado");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo eliminar");
        }
    };

    return (
        <div data-testid="admin-users">
            <PageHeader
                kicker="Team"
                title="Admin users"
                action={
                    <Button
                        data-testid="user-create-btn"
                        onClick={() => { setForm(emptyForm()); setOpen(true); }}
                        className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                    >
                        <Plus size={16} weight="bold" className="mr-1.5" /> Add user
                    </Button>
                }
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((u) => (
                    <article
                        key={u.id}
                        data-testid={`user-${u.id}`}
                        className="bg-white rounded-2xl border border-border p-5"
                    >
                        <div className="flex items-start gap-3">
                            <span className={`grid place-items-center w-12 h-12 rounded-full ${
                                u.role === "owner" ? "bg-primary text-primary-foreground" : "bg-muted"
                            }`}>
                                {u.role === "owner" ? <Crown size={20} weight="fill" /> : <User size={20} weight="fill" />}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="font-display font-black text-lg truncate">
                                    {u.name || u.email.split("@")[0]}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest bg-foreground text-background rounded-full px-2 py-0.5">
                                    {u.role}
                                </span>
                            </div>
                            <div className="flex flex-col gap-1">
                                {u.role !== "owner" && (
                                    <button
                                        data-testid={`edit-user-${u.id}`}
                                        onClick={() => openEdit(u)}
                                        className="h-8 w-8 grid place-items-center rounded-full hover:bg-muted text-foreground"
                                        title="Editar permisos"
                                    >
                                        <PencilSimple size={14} weight="bold" />
                                    </button>
                                )}
                                {u.email !== user.email && (
                                    <button
                                        data-testid={`delete-user-${u.id}`}
                                        onClick={() => remove(u)}
                                        className="h-8 w-8 grid place-items-center rounded-full hover:bg-red-50 text-destructive"
                                        title="Eliminar"
                                    >
                                        <Trash size={14} weight="bold" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="mt-4">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                                <ShieldCheck size={12} weight="bold" /> Acceso
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {u.role === "owner" ? (
                                    <span className="text-[10px] font-bold uppercase tracking-widest bg-primary/10 text-primary rounded-full px-2 py-0.5">
                                        Acceso total
                                    </span>
                                ) : (u.modules || []).length === 0 ? (
                                    <span className="text-[10px] text-muted-foreground italic">Sin módulos</span>
                                ) : (
                                    (u.modules || []).map((m) => (
                                        <span
                                            key={m}
                                            data-testid={`user-${u.id}-module-${m}`}
                                            className="text-[10px] font-bold bg-muted rounded-full px-2 py-0.5"
                                        >
                                            {MODULE_LABELS[m] || m}
                                        </span>
                                    ))
                                )}
                            </div>
                        </div>

                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-4">
                            Added {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        </p>
                    </article>
                ))}
            </div>

            {/* Create user dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="user-dialog" className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">Nuevo usuario admin</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            data-testid="user-name"
                            placeholder="Nombre (opcional)"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <Input
                            data-testid="user-email"
                            type="email"
                            placeholder="Email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />
                        <Input
                            data-testid="user-password"
                            type="password"
                            placeholder="Contraseña (mín. 6 caracteres)"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                        />
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                                Rol
                            </label>
                            <select
                                data-testid="user-role"
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            >
                                <option value="staff">Staff (módulos limitados)</option>
                                <option value="owner">Owner (acceso completo)</option>
                            </select>
                        </div>

                        {form.role === "staff" && (
                            <div data-testid="user-modules-picker" className="rounded-2xl border border-border bg-muted/40 p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1">
                                        <ShieldCheck size={12} weight="bold" /> Módulos permitidos
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            data-testid="user-modules-all"
                                            onClick={() => setForm((f) => ({ ...f, modules: [...availableModules] }))}
                                            className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
                                        >
                                            Todos
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="user-modules-none"
                                            onClick={() => setForm((f) => ({ ...f, modules: [] }))}
                                            className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:underline"
                                        >
                                            Ninguno
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {availableModules.map((m) => (
                                        <label
                                            key={m}
                                            data-testid={`user-module-${m}`}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-border cursor-pointer text-sm"
                                        >
                                            <Checkbox
                                                checked={form.modules.includes(m)}
                                                onCheckedChange={() => toggleFormModule(m)}
                                            />
                                            <span className="font-medium">{MODULE_LABELS[m] || m}</span>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-2">
                                    El staff solo verá en el panel los módulos seleccionados.
                                </p>
                            </div>
                        )}
                        {form.role === "owner" && (
                            <p className="text-[11px] text-muted-foreground bg-primary/5 border border-primary/20 rounded-xl px-3 py-2">
                                Los owners tienen acceso completo a todos los módulos automáticamente.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            data-testid="user-cancel"
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={create}
                            disabled={busy}
                            data-testid="user-save"
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            {busy ? "..." : "Crear"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit modules dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="edit-modules-dialog" className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            Editar permisos — {editing?.name || editing?.email}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="rounded-2xl border border-border bg-muted/40 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1">
                                <ShieldCheck size={12} weight="bold" /> Módulos permitidos
                            </p>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setEditModules([...availableModules])}
                                    className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
                                >
                                    Todos
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditModules([])}
                                    className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:underline"
                                >
                                    Ninguno
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {availableModules.map((m) => (
                                <label
                                    key={m}
                                    data-testid={`edit-module-${m}`}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-border cursor-pointer text-sm"
                                >
                                    <Checkbox
                                        checked={editModules.includes(m)}
                                        onCheckedChange={() => toggleEditModule(m)}
                                    />
                                    <span className="font-medium">{MODULE_LABELS[m] || m}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditing(null)}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={saveModules}
                            disabled={busy}
                            data-testid="edit-modules-save"
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            {busy ? "..." : "Guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
